import { ApiReportSectionFieldsFieldTypeChoices, ReportSectionFieldsType } from "@api/graphql/types-and-hooks";
import { isWetSignatureContentEmpty } from "@components/ReportField/inputs/wetSignatureContent";
import { ReportTemplateSectionField } from "@providers/TemplateEditorProvider";

export const getRadioAndSelectFieldAnswer = (input: string | undefined | null): string => {
  if (!input) return "";
  try {
    const parsedInput = JSON.parse(input);
    return parsedInput?.answer ?? "";
  } catch {
    return "";
  }
};

export const getCheckboxFieldAnswer = (input: string | undefined | null): string => {
  if (!input) return "";
  try {
    const parsedInput = JSON.parse(input);
    return parsedInput?.answer.join(", ") ?? "";
  } catch {
    return "";
  }
};

// Strips residual editor markup that the WYSIWYG can leave behind when a
// user clicks in/out without actually typing (empty paragraphs, stray
// <br>, &nbsp;). Without this, those visually-blank fields would count as
// "filled" and silently slip past the required-field gate.
// Approach: parse as HTML and read textContent — this handles nested
// markup like `<p><br></p>` without depending on regex strip order, and
// avoids the regex-on-HTML pitfall that SonarCloud flags as a hotspot.
const stripEmptyWysiwygMarkup = (content: string): string => {
  const doc = new DOMParser().parseFromString(content, "text/html");
  return (doc.body.textContent ?? "").trim();
};

export const isFieldContentEmpty = (
  fieldType: ApiReportSectionFieldsFieldTypeChoices,
  content: string | undefined | null,
): boolean => {
  if (
    fieldType === ApiReportSectionFieldsFieldTypeChoices.Select ||
    fieldType === ApiReportSectionFieldsFieldTypeChoices.Radio
  ) {
    const answer = getRadioAndSelectFieldAnswer(content);
    // A multi-select stores its answer as an array; `[]` is a truthy empty
    // value, so guard the array case explicitly before the falsy check.
    return Array.isArray(answer) ? answer.length === 0 : !answer;
  }
  if (fieldType === ApiReportSectionFieldsFieldTypeChoices.Checkbox) {
    return !getCheckboxFieldAnswer(content);
  }
  if (fieldType === ApiReportSectionFieldsFieldTypeChoices.Wysiwyg) {
    return !content || stripEmptyWysiwygMarkup(content) === "";
  }
  if (fieldType === ApiReportSectionFieldsFieldTypeChoices.WetSignature) {
    // `content` always holds at least `allowed_methods` once a template
    // configures a WET_SIGNATURE field, so the generic falsy-content check
    // below would treat it as filled before anyone signed — mirrors the
    // backend's `is_wet_signature_field_empty` (design.md Decision 4 of
    // `unify-wet-signature-field-type`).
    return isWetSignatureContentEmpty(content);
  }
  return !content || content.trim() === "";
};

export function parseJson<T>(input: string | undefined | null): T | null {
  if (!input) return null;

  try {
    return JSON.parse(input) as T;
  } catch {
    return null;
  }
}

export const getFieldType = (field: ReportTemplateSectionField | ReportSectionFieldsType): string => {
  return field.fieldType || "";
};

export const getFieldKey = (field: ReportTemplateSectionField | ReportSectionFieldsType): string | undefined => {
  return field.fieldIdentifier || undefined;
};

export const getFieldAiGenerated = (
  field: ReportTemplateSectionField | ReportSectionFieldsType,
): boolean | undefined => {
  // Both types can have aiGenerated, but ReportSectionFieldsType has it as required
  if ("aiSectionField" in field) {
    return !!(field.aiGenerated || field.aiSources || (field as ReportSectionFieldsType).aiSectionField);
  }
  return !!(field.aiGenerated || field.aiSources);
};

export const getFieldImageSignedUrl = (
  field: ReportTemplateSectionField | ReportSectionFieldsType,
): string | undefined => {
  // Both types can have imageSignedUrl, handle Maybe<string> type
  return field.imageSignedUrl || undefined;
};
